import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ViewListIcon from "@mui/icons-material/ViewList";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useIntegration } from "../hooks/useIntegration";
import { useUserMsp } from "../hooks/useUserMsp";
import { Customer } from "types/integration";
import { isMspOrgCustomer } from "../utils/mspOrgCustomer";
import { NeoButton } from "../shared/components";
import CustomWorkflowApi, { WORKFLOW_TYPES, WorkflowType } from "../apis/customWorkflowApi";
import IntegrationApi from "../apis/integrationApi";
import { useAppSelector } from "../hooks";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import CustomWorkflowFlowDiagram from "../components/customWorkflow/CustomWorkflowFlowDiagram";
import AddToGroupsModal, { AddToGroupsConfig } from "../components/customWorkflow/AddToGroupsModal";
import AddToSharePointSitesModal, {
  AddToSharePointSitesConfig,
} from "../components/customWorkflow/AddToSharePointSitesModal";
import StepConfigModal from "../components/customWorkflow/StepConfigModal";
import TechnicianChecklistModal, {
  TechnicianChecklistItem,
} from "../components/customWorkflow/TechnicianChecklistModal";
import {
  DEFAULT_WELCOME_BODY_TEMPLATE,
  DEFAULT_WELCOME_SUBJECT_TEMPLATE,
} from "../components/customWorkflow/welcomeEmailDefaults";
import { isSchedulerLdEnabled } from "../utils/schedulerAccess";
import { stripLeadingWorkflowStepNumber } from "../utils/stripLeadingWorkflowStepNumber";
import { usePublishConfigurationSupport } from "../configurationSupport/ConfigurationSupportContext";

const customWorkflowApi = new CustomWorkflowApi();
const integrationApi = new IntegrationApi();
const TECHNICIAN_CHECKLIST_ACTION = "technician_checklist";
const TECHNICIAN_CHECKLIST_OPTIONS_KEY = "technician_tasks_json";

// Template step definitions with their action names, option keys, and M365 fields updated
const TEMPLATE_STEPS = [
  {
    id: "create_user",
    action: "create_user",
    label: "Create user",
    options: {
      formulate_email_from_name: "false",
      email_format: "firstname.lastname",
      email_custom_format: "",
      email_domain: "",
      use_custom_password: "false",
      custom_password: "",
      force_change_next_sign_in: "true",
      generate_onetime_link: "false",
      check_recent_duplicates: "true",
      verify_authorized_requester: "false",
      authorized_requesters: "",
    },
    fields: "display name, mail alias, UPN, password, usage location, job title, department",
  },
  {
    id: "set_contact_info",
    action: "m365_update_contact_info",
    label: "Set contact info",
    fields: "mobile phone, business phones, fax, other emails",
  },
  {
    id: "set_org_info",
    action: "m365_update_organizational_info",
    label: "Set org info",
    fields: "job title, department, company, office location, employee ID/type, cost center, division, hire date",
  },
  {
    id: "set_personal_info",
    action: "m365_update_personal_information",
    label: "Set personal info",
    fields: "display name, first/last name, address, city, state, postal code, country, language, usage location",
  },
  {
    id: "assign_license",
    action: "m365_assign_license_to_user",
    label: "Assign license",
    options: {
      default_license: "",
      technician_instruction:
        "Use m365_get_user_licenses / m365_list_available_licenses to resolve sku_id(s) per row. Pass license_input.licenses (one or more entries, optional disabled_plans per row) in one Graph assignLicense call.",
    },
    fields: "Assign subscription seats.",
  },
  {
    id: "set_email_signature",
    action: "m365_set_email_signature",
    label: "Set email signature",
    options: {
      signature_html_template: "",
      auto_add_to_new: "true",
      auto_add_to_reply: "true",
      auto_add_to_forward: "false",
      auto_add_on_mobile: "false",
      technician_instruction: "",
    },
    fields: "Outlook signature HTML (Exchange Online), auto-add toggles",
  },
  {
    id: "assign_groups",
    action: "m365_add_user_to_group",
    label: "Assign groups",
    options: { default_group_names: "", technician_instruction: "" },
    fields: "group membership (user added to specified groups)",
  },
  {
    id: "set_manager",
    action: "m365_set_manager",
    label: "Set manager",
    options: { default_manager_hint: "", technician_instruction: "" },
    fields: "manager (UPN or name from ticket / hint)",
  },
  {
    id: "add_to_team",
    action: "m365_add_user_to_team",
    label: "Add to Microsoft Team",
    options: { default_team_hint: "", technician_instruction: "" },
    fields: "Teams membership (team name resolved via search)",
  },
  {
    id: "assign_sharepoint_site",
    action: "add_to_sharepoint_site",
    label: "Assign SharePoint site",
    options: { default_sites: "", default_role: "read", technician_instruction: "" },
    fields: "site/folder access, role (read/write)",
  },
  {
    id: "itglue_add_password",
    action: "itglue_create_password",
    label: "IT Glue: save user password",
    options: {
      itglue_organization_hint: "",
      itglue_password_name_template: "{{display_name}} — Microsoft 365",
      itglue_notes_template: "Onboarded M365 user. UPN: {{user_email}}",
      itglue_url: "",
      itglue_password_folder_name: "",
      itglue_password_category_name: "",
      technician_instruction: "",
    },
    fields: "IT Glue password for the new user (after password is set)",
  },
  {
    id: "send_welcome_email",
    // Uses the template-only `compose_email` utility action (always HITL; technician sends from
    // their own account). Legacy Graph/Gmail send actions are disabled at the DB level.
    action: "compose_email",
    label: "Send welcome email",
    options: {
      welcome_subject_template: DEFAULT_WELCOME_SUBJECT_TEMPLATE,
      welcome_body_template: DEFAULT_WELCOME_BODY_TEMPLATE,
      welcome_body_format: "Text",
      welcome_recipient: "new_user",
      welcome_cc_recipients: "",
    },
    fields: "welcome letter (subject/body templates with {{placeholders}})",
  },
  {
    id: "notify_technician",
    action: "msp_notify_technician",
    label: "Notify technician (MSP tenant)",
    defaultEnabled: false,
    options: {
      recipient: "assignee",
      message_template: "Theo needs attention on {{ticket_number}} {{ticket_title}}",
    },
    fields: "Teams/email notify using the MSP organization Microsoft 365 tenant",
  },
  {
    id: "send_email_from_msp",
    action: "msp_send_email",
    label: "Send email from MSP",
    defaultEnabled: false,
    options: {
      to_recipients: "",
      subject_template: "Update on {{ticket_number}}",
      body_template: "",
      from_mailbox: "",
    },
    fields: "Send mail from the MSP support mailbox via Graph",
  },
];

/** Template row for onboarding or offboarding (optional defaultEnabled for offboarding-only). */
type TemplateStepDef = {
  id: string;
  action: string;
  label: string;
  options?: Record<string, string>;
  fields?: string;
  defaultEnabled?: boolean;
};

/**
 * Standard M365 offboarding — maps to Graph / Exchange / IT Glue tools where available.
 * Manual items (shared mailbox conversion, Enterprise App OAuth review, Teams Phone) are called out in the form UI.
 * Steps 1–2 order is mandatory (block sign-in, then revoke sessions).
 */
const OFFBOARDING_TEMPLATE_STEPS: TemplateStepDef[] = [
  {
    id: "offboard_block_sign_in",
    action: "m365_block_unblock_user",
    label: "Block sign-in (disable Entra account)",
    options: {
      block_account: "true",
      technician_instruction:
        "Call m365_block_unblock_user with block=true for the departing user's UPN or object id immediately — before any other offboarding step.",
    },
    fields: "Disables sign-in in Entra ID (accountEnabled=false). Must run first.",
  },
  {
    id: "offboard_revoke_sessions",
    action: "m365_revoke_user_session",
    label: "Revoke all sign-in sessions",
    options: {
      technician_instruction:
        "Invoke m365_revoke_user_session for the same user right after blocking (revokes refresh tokens / sessions). Mandatory second step.",
    },
    fields: "Ends active browser, app, and mobile sessions.",
  },
  {
    id: "offboard_reset_password",
    action: "m365_reset_password",
    label: "Reset password",
    options: {
      use_custom_password: "false",
      force_change_next_sign_in: "false",
      generate_onetime_link: "false",
    },
    fields: "New strong password after lockout; complements block + session revoke.",
  },
  {
    id: "offboard_list_auth_methods",
    action: "m365_get_user_auth_details",
    label: "List authentication / MFA methods",
    options: {
      technician_instruction:
        "Use m365_list_user_authentication_methods to list methods (Authenticator, phone, FIDO, etc.) before removing them.",
    },
    fields: "Enumerates MFA methods for targeted removal.",
  },
  {
    id: "offboard_remove_mfa",
    action: "m365_delete_authentication_method",
    label: "Remove authentication methods",
    options: {
      technician_instruction:
        "For each non-password method from m365_list_user_authentication_methods, call m365_delete_authentication_method with user_id and method_id. Repeat per policy.",
    },
    fields: "Removes authenticator, phone, FIDO, etc., using Graph method ids.",
  },
  {
    id: "offboard_set_out_of_office",
    action: "m365_set_mailbox_settings",
    label: "Set automatic replies (Out of office)",
    options: {
      technician_instruction:
        "Use m365_set_mailbox_settings with automatic reply fields for the leaver's mailbox. Merge ticket + workflow option text for internal/external messages.",
    },
    fields: "Mailbox automatic replies via Graph.",
  },
  {
    id: "offboard_set_forwarding",
    action: "m365_set_forwarding_settings",
    label: "Set mail forwarding (Exchange Online)",
    options: {
      technician_instruction:
        "Requires Exchange Online app certificate on the integration. Use m365_set_forwarding_settings with user_email and forwarding_address from ticket/hint. Full access delegation may still require Exchange Admin Center.",
    },
    fields: "Exchange Online PowerShell forwarding.",
  },
  {
    id: "offboard_grant_onedrive_access",
    action: "m365_grant_onedrive_access",
    label: "Grant successor access to OneDrive",
    options: {
      technician_instruction:
        "m365_grant_onedrive_access: owner_email = leaver UPN; grantee_emails = manager/successor; item_id = root; permission read or write per policy.",
    },
    fields: "OneDrive root invite for data handover (watch retention / 30-day window).",
  },
  {
    id: "offboard_transfer_team_ownership",
    action: "m365_add_owner_to_group",
    label: "Transfer Microsoft Team / group ownership",
    options: {
      technician_instruction:
        "Teams use M365 groups. Resolve team/group with m365_search_teams or m365_search_groups; m365_add_owner_to_group for successor; then remove leaver as owner/member when safe.",
    },
    fields: "Add successor owner before removing leaver from team-connected groups.",
  },
  {
    id: "offboard_list_group_memberships",
    action: "m365_list_user_groups",
    label: "List directory group memberships",
    options: {
      technician_instruction:
        "m365_list_user_groups for the leaver; use output for targeted m365_remove_user_from_group calls.",
    },
    fields: "DLs, Microsoft 365 groups, security groups, etc.",
  },
  {
    id: "offboard_remove_from_groups",
    action: "m365_remove_user_from_group",
    label: "Remove from groups & mail-enabled groups",
    options: {
      technician_instruction:
        "For each group_id from the list step or ticket, m365_remove_user_from_group. Skip dynamic-membership and directory-synchronized groups, plus groups that must stay until mailbox work completes.",
    },
    fields: "Clean up distribution lists and shared mailbox membership where applicable.",
  },
  {
    id: "offboard_convert_shared_mailbox",
    action: "m365_convert_mailbox_type",
    label: "Convert mailbox to shared",
    defaultEnabled: false,
    options: {
      technician_instruction:
        'Call m365_convert_mailbox_type with mailbox_identity = the departing user\'s UPN and target_type = "Shared". Run after mailbox handover (out-of-office, forwarding, OneDrive) and before removing licenses so a manager/successor keeps mail access. Shared mailboxes under 50 GB do not require a license.',
    },
    fields: "Converts the leaver's mailbox to shared (Set-Mailbox -Type Shared). Enable when the mailbox is retained; run before license removal.",
  },
  {
    id: "offboard_remove_licenses",
    action: "m365_remove_license_from_user",
    label: "Remove Microsoft 365 licenses",
    options: {
      technician_instruction:
        "Typically after shared mailbox conversion. Use m365_get_user_licenses / m365_list_available_licenses to resolve skuId(s); pass sku_ids (one or more GUIDs) in one Graph assignLicense call.",
    },
    fields: "Reclaim subscription seats.",
  },
  {
    id: "offboard_itglue_documentation",
    action: "itglue_create_document",
    label: "Document offboarding in IT Glue",
    defaultEnabled: false,
    options: {
      itglue_organization_hint: "",
      document_title_template: "Offboarding — {{upn}}",
      document_body_notes: "Date, mailbox disposition, OneDrive handover, license status, team ownership.",
      technician_instruction:
        "Resolve organization_id via itglue_search_organizations; create document with title/body from templates + ticket.",
    },
    fields: "IT Glue document for audit trail.",
  },
  {
    id: "offboard_delete_user",
    action: "m365_delete_user",
    label: "Delete Entra user (scheduled / final)",
    defaultEnabled: false,
    options: {
      technician_instruction:
        "Use only after policy cooling-off (e.g. 30 days). m365_delete_user is destructive; confirm with technician.",
    },
    fields: "Hard delete user — optional final step.",
  },
  {
    id: "notify_technician",
    action: "msp_notify_technician",
    label: "Notify technician (MSP tenant)",
    defaultEnabled: false,
    options: {
      recipient: "assignee",
      message_template: "Offboarding update for {{ticket_number}} {{ticket_title}}",
    },
    fields: "Teams/email notify using the MSP organization Microsoft 365 tenant",
  },
  {
    id: "send_email_from_msp",
    action: "msp_send_email",
    label: "Send email from MSP",
    defaultEnabled: false,
    options: {
      to_recipients: "",
      subject_template: "Offboarding update on {{ticket_number}}",
      body_template: "",
      from_mailbox: "",
    },
    fields: "Send mail from the MSP support mailbox via Graph",
  },
];

// Default value fields per step (key = M365 input field name, label, placeholder)
type DefaultFieldSpec = { key: string; label: string; placeholder?: string; multiline?: boolean; minRows?: number };
const STEP_DEFAULT_FIELDS: Record<string, DefaultFieldSpec[]> = {
  create_user: [
    { key: "usageLocation", label: "Usage location", placeholder: "e.g. US" },
    { key: "jobTitle", label: "Job title", placeholder: "e.g. Developer" },
    { key: "department", label: "Department", placeholder: "e.g. Engineering" },
  ],
  set_password: [], // Uses custom UI (toggles + password field)
  set_contact_info: [
    { key: "mobile_phone", label: "Mobile phone", placeholder: "+1-555-000-0000" },
    { key: "business_phones", label: "Business phones", placeholder: "Comma separated" },
    { key: "fax_number", label: "Fax number" },
    { key: "other_mails", label: "Other emails", placeholder: "Comma separated" },
  ],
  set_org_info: [
    { key: "job_title", label: "Job title" },
    { key: "department", label: "Department" },
    { key: "company_name", label: "Company name" },
    { key: "office_location", label: "Office location" },
    { key: "employee_id", label: "Employee ID" },
    { key: "employee_type", label: "Employee type" },
    { key: "cost_center", label: "Cost center" },
    { key: "division", label: "Division" },
    { key: "employee_hire_date", label: "Hire date", placeholder: "ISO 8601, e.g. 2024-01-15" },
  ],
  set_personal_info: [
    { key: "display_name", label: "Display name" },
    { key: "given_name", label: "First name" },
    { key: "surname", label: "Last name" },
    { key: "street_address", label: "Street address" },
    { key: "city", label: "City" },
    { key: "state", label: "State" },
    { key: "postal_code", label: "Postal code" },
    { key: "country", label: "Country" },
    { key: "preferred_language", label: "Language", placeholder: "e.g. en-US" },
    { key: "usage_location", label: "Usage location" },
  ],
  assign_license: [{ key: "default_license", label: "Default license", placeholder: "e.g. Microsoft 365 Business Premium" }],
  assign_groups: [{ key: "default_group_names", label: "Default group names", placeholder: "Comma separated" }],
  set_manager: [],
  add_to_team: [],
  assign_sharepoint_site: [
    { key: "default_sites", label: "Default sites", placeholder: "Comma separated site names/URLs" },
    { key: "default_role", label: "Default role", placeholder: "read or write" },
  ],
  itglue_add_password: [],
  notify_technician: [
    { key: "recipient", label: "Recipient (assignee or UPN)", placeholder: "assignee or tech@yourmsp.com" },
    {
      key: "message_template",
      label: "Message template",
      placeholder: "Theo needs attention on {{ticket_number}}",
      multiline: true,
      minRows: 3,
    },
  ],
  send_email_from_msp: [
    { key: "to_recipients", label: "To (comma-separated)", placeholder: "{{ticket_requester_psa_email}}" },
    { key: "subject_template", label: "Subject template", placeholder: "Update on {{ticket_number}}" },
    { key: "body_template", label: "Body template", multiline: true, minRows: 4 },
    { key: "from_mailbox", label: "From mailbox (optional override)", placeholder: "Uses Teams sender or organization default if blank" },
  ],
};

const OFFBOARDING_STEP_DEFAULT_FIELDS: Record<string, DefaultFieldSpec[]> = {
  offboard_set_out_of_office: [
    { key: "ooo_internal_message", label: "Internal auto-reply text", placeholder: "Optional; otherwise derive from ticket" },
    { key: "ooo_external_message", label: "External auto-reply text", placeholder: "Optional" },
  ],
  offboard_set_forwarding: [
    { key: "forward_to_email", label: "Forward mail to (email/UPN)", placeholder: "Successor or shared mailbox" },
  ],
  offboard_grant_onedrive_access: [
    { key: "grant_to_upn", label: "Grant OneDrive access to (UPN)", placeholder: "Manager / successor email" },
  ],
  offboard_transfer_team_ownership: [
    { key: "successor_owner_upn", label: "New owner UPN (Teams/groups)", placeholder: "Successor" },
  ],
  offboard_remove_from_groups: [
    { key: "group_removal_hint", label: "Groups to leave (hint)", placeholder: "Optional; prefer ids from list step" },
  ],
  offboard_remove_licenses: [
    { key: "license_sku_hint", label: "License / SKU hint to remove", placeholder: "Optional; else use get_user_licenses" },
  ],
  offboard_itglue_documentation: [
    { key: "itglue_organization_hint", label: "IT Glue org search hint", placeholder: "Company name" },
    { key: "document_title_template", label: "Document title template", placeholder: "Offboarding — {{upn}}" },
    { key: "document_body_notes", label: "Document body / notes", placeholder: "What was retained, who has access" },
  ],
};

function getTemplateDefinitions(workflowType: WorkflowType): TemplateStepDef[] {
  return workflowType === "User Onboarding" ? (TEMPLATE_STEPS as TemplateStepDef[]) : OFFBOARDING_TEMPLATE_STEPS;
}

/** When workflow_type was never stored (legacy) or defaulted wrong, infer from saved steps. */
function inferWorkflowTypeFromSavedSteps(steps: unknown[] | undefined): WorkflowType | null {
  if (!Array.isArray(steps)) return null;
  for (const raw of steps) {
    const st = raw as { action?: string; id?: string };
    const action = String(st?.action || "");
    const sid = String(st?.id || "");
    if (action === "m365_block_unblock_user" || action === "m365_revoke_user_session") return "User Offboarding";
    if (sid.startsWith("offboard_")) return "User Offboarding";
    if (action === "m365_get_user_auth_details" || action === "m365_delete_authentication_method") {
      return "User Offboarding";
    }
  }
  return null;
}

function getStepDefaultFieldSpecs(stepId: string): DefaultFieldSpec[] {
  return STEP_DEFAULT_FIELDS[stepId] ?? OFFBOARDING_STEP_DEFAULT_FIELDS[stepId] ?? [];
}

type TemplateStepState = {
  id: string;
  action: string;
  label: string;
  enabled: boolean;
  options?: Record<string, string>;
  fields?: string;
};

function buildTemplateStepsFromDefinitions(
  defs: TemplateStepDef[],
  kind: "onboarding" | "offboarding"
): TemplateStepState[] {
  return defs.map((t) => {
    const defaultOpts: Record<string, string> = t.options
      ? (Object.fromEntries(Object.entries(t.options).map(([k, v]) => [k, String(v ?? "")])) as Record<string, string>)
      : {};
    const fieldSpecs = getStepDefaultFieldSpecs(t.id);
    for (const s of fieldSpecs) {
      if (!(s.key in defaultOpts)) defaultOpts[s.key] = "";
    }
    const enabled =
      kind === "onboarding"
        ? t.id !== "send_welcome_email" && t.defaultEnabled !== false
        : t.defaultEnabled !== false;
    return {
      id: t.id,
      action: t.action,
      label: t.label,
      enabled,
      options: Object.keys(defaultOpts).length > 0 ? defaultOpts : undefined,
      fields: t.fields,
    };
  });
}

type CustomStepState = {
  id: string;
  action: string;
  isCustom: true;
  options?: Record<string, string>;
};

const CustomWorkflowForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { customers, getCustomers } = useIntegration();
  const { userMspInfo } = useUserMsp();
  const user = useAppSelector((state: any) => state.auth?.user);
  const flags = useFeatureFlag();
  const schedulerEnabled = isSchedulerLdEnabled(flags as Record<string, unknown>);

  type CustomWorkflowsLocationState = {
    selectedCustomer?: string;
    cloneFromId?: number;
    /** Query string from the workflows list (e.g. `?customer=…`) so Back / post-save return preserves filters. */
    customWorkflowsListSearch?: string;
  };

  const navState = location.state as CustomWorkflowsLocationState | null | undefined;
  const preselectedCustomer = navState?.selectedCustomer;
  const cloneFromId = !isEdit ? navState?.cloneFromId : undefined;
  const isClone = Boolean(cloneFromId);
  const customWorkflowsListSearch = navState?.customWorkflowsListSearch;

  const filteredCustomers = useMemo(() => {
    const allCustomers = (customers?.rows || customers || []) as Customer[];
    const mspCustomers = allCustomers.filter(
      (c: Customer) =>
        c.name?.toLowerCase() === "root" ||
        String((c as any).msp_id) === String(userMspInfo?.msp_id)
    );
    const rootCustomers = mspCustomers.filter((c: Customer) => c.name?.toLowerCase() === "root");
    const otherCustomers = mspCustomers.filter(
      (c: Customer) => c.name?.toLowerCase() !== "root" && !isMspOrgCustomer(c)
    );
    return [...rootCustomers, ...otherCustomers];
  }, [customers, userMspInfo?.msp_id]);

  const rootCustomerId = useMemo(() => {
    const root = filteredCustomers.find((c: Customer) => c.name?.toLowerCase() === "root");
    return root?.id?.toString() || "";
  }, [filteredCustomers]);

  const initialCustomerId = useMemo(() => {
    if (preselectedCustomer === "all" || !preselectedCustomer) return rootCustomerId;
    return preselectedCustomer;
  }, [preselectedCustomer, rootCustomerId]);

  const [workflowName, setWorkflowName] = useState("");
  const [workflowType, setWorkflowType] = useState<WorkflowType>("User Onboarding");
  const [autoContinueHitl, setAutoContinueHitl] = useState(false);
  const [customerId, setCustomerId] = useState<string>("");
  const hasInitializedCustomer = useRef(false);
  const [viewMode, setViewMode] = useState<"list" | "flow">("flow");
  const [templateSteps, setTemplateSteps] = useState<TemplateStepState[]>(() =>
    buildTemplateStepsFromDefinitions(TEMPLATE_STEPS as TemplateStepDef[], "onboarding")
  );
  const [customSteps, setCustomSteps] = useState<CustomStepState[]>([]);
  const [stepConfigModal, setStepConfigModal] = useState<{ type: "template"; index: number } | { type: "custom"; index: number } | null>(null);
  const [addToGroupsModalOpen, setAddToGroupsModalOpen] = useState(false);
  const [addToSharePointModalOpen, setAddToSharePointModalOpen] = useState(false);
  const [technicianChecklistStepIndex, setTechnicianChecklistStepIndex] = useState<number | null>(null);
  const [customerDomain, setCustomerDomain] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "info" | "success" | "warning" | "error" }>({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  // Fetch customer domain from M365/GWorkspace config for Create user step
  useEffect(() => {
    if (!customerId || customerId === rootCustomerId) {
      setCustomerDomain("");
      return;
    }
    integrationApi
      .getApplicationsByCustomer(customerId)
      .then((res: any) => {
        const apps = Array.isArray(res) ? res : (res?.rows || res?.applications || []);
        const normalizeAppName = (n: string) => String(n || "").toLowerCase().replace(/\s+/g, "");
        const m365 = apps.find((a: any) => {
          const name = normalizeAppName(a?.name || "");
          return name === "m365" || name === "microsoft365";
        });
        const gworkspace = apps.find((a: any) => {
          const name = normalizeAppName(a?.name || "");
          return name === "gworkspace" || name === "googleworkspace";
        });
        const extractDomain = (config: any): string => {
          if (!config) return "";
          const c = typeof config === "string" ? (() => { try { return JSON.parse(config); } catch { return {}; } })() : config;
          return (c.tenant_domain || (c.admin_email && String(c.admin_email).split("@")[1]) || "").trim();
        };
        const domain = extractDomain(m365?.customer_config) || extractDomain(gworkspace?.customer_config);
        setCustomerDomain(domain || "");
      })
      .catch(() => setCustomerDomain(""));
  }, [customerId, rootCustomerId]);

  // Set initial customer once when customers load - never overwrite after user selects
  useEffect(() => {
    if (isEdit || hasInitializedCustomer.current) return;
    if (filteredCustomers.length > 0) {
      const defaultId = initialCustomerId || filteredCustomers[0]?.id?.toString() || "";
      setCustomerId(defaultId);
      hasInitializedCustomer.current = true;
    }
  }, [isEdit, filteredCustomers.length, initialCustomerId, filteredCustomers]);

  // Editing and copying share the same hydration path. Copy mode retains the
  // selected target customer and saves through the create endpoint.
  useEffect(() => {
    const sourceWorkflowId = isEdit ? id : cloneFromId;
    if (!sourceWorkflowId) return;
    customWorkflowApi
      .getWorkflowById(sourceWorkflowId)
      .then((wf: any) => {
        setWorkflowName(wf?.workflow_name || "");
        if (isClone) {
          if (preselectedCustomer) {
            setCustomerId(preselectedCustomer);
            hasInitializedCustomer.current = true;
          }
        } else {
          setCustomerId(wf?.customer_id?.toString() || "");
        }

        let parsedStepsJson: { steps?: unknown[]; auto_continue_hitl?: boolean } | null = null;
        const sj = wf?.steps_json;
        if (sj) {
          try {
            parsedStepsJson = typeof sj === "string" ? JSON.parse(sj) : sj;
          } catch {
            parsedStepsJson = null;
          }
        }
        setAutoContinueHitl(parsedStepsJson?.auto_continue_hitl === true);
        const savedSteps = Array.isArray(parsedStepsJson?.steps) ? parsedStepsJson!.steps! : undefined;

        let loadedWfType: WorkflowType = WORKFLOW_TYPES.includes(wf?.workflow_type as WorkflowType)
          ? (wf.workflow_type as WorkflowType)
          : "User Onboarding";
        const inferredType = inferWorkflowTypeFromSavedSteps(savedSteps);
        if (
          inferredType === "User Offboarding" &&
          (loadedWfType === "User Onboarding" || !wf?.workflow_type)
        ) {
          loadedWfType = "User Offboarding";
        }
        setWorkflowType(loadedWfType);

        if (savedSteps && savedSteps.length > 0) {
          try {
            const defs = getTemplateDefinitions(loadedWfType);
            const loadedTemplate = buildTemplateStepsFromDefinitions(
              defs,
              loadedWfType === "User Onboarding" ? "onboarding" : "offboarding"
            ).map((t) => ({ ...t, enabled: false }));
            const loadedCustom: CustomStepState[] = [];
            const templateActions = new Set(defs.map((d) => d.action));
            let legacyVerifyOptions: Record<string, string> | null = null;
            for (const st of savedSteps as { action?: string; id?: string; options?: object }[]) {
              const action = String(st.action || "");
              // Onboarding: password is already handled by the Create user step.
              // Drop any legacy standalone reset-password step instead of treating it as a custom step.
              if (loadedWfType === "User Onboarding" && action === "m365_reset_password") {
                continue;
              }
              // Requester verification is now part of the Create user step. Drop the legacy
              // standalone step and migrate its authorized list onto Create user (below).
              if (action === "verify_authorized_requester") {
                if (!legacyVerifyOptions && st.options && typeof st.options === "object") {
                  legacyVerifyOptions = st.options as Record<string, string>;
                }
                continue;
              }
              const templateIdx = loadedTemplate.findIndex((t) => t.action === action);
              if (templateIdx >= 0) {
                loadedTemplate[templateIdx].enabled = true;
                if (st.options && typeof st.options === "object") {
                  let merged = { ...loadedTemplate[templateIdx].options, ...st.options } as Record<string, string>;
                  // Migrate legacy set_password options (mode/password) to toggles
                  if (loadedTemplate[templateIdx].action === "m365_reset_password" && ("mode" in merged || "password" in merged)) {
                    const mode = merged.mode || "one_time_link";
                    merged = { ...merged };
                    if (!("use_custom_password" in merged)) merged.use_custom_password = mode === "custom_password" ? "true" : "false";
                    if (!("generate_onetime_link" in merged)) merged.generate_onetime_link = mode === "one_time_link" ? "true" : "false";
                    if (!("force_change_next_sign_in" in merged)) merged.force_change_next_sign_in = "false";
                    if (merged.password && !merged.custom_password) merged.custom_password = merged.password;
                    delete merged.mode;
                    delete merged.password;
                  }
                  loadedTemplate[templateIdx].options = merged;
                }
              } else if (action && !templateActions.has(action)) {
                loadedCustom.push({
                  id: st.id || `step_${Date.now()}`,
                  action:
                    action === TECHNICIAN_CHECKLIST_ACTION
                      ? "Technician checklist"
                      : action.replace(/_/g, " "),
                  isCustom: true,
                  options: st.options && typeof st.options === "object" ? (st.options as Record<string, string>) : undefined,
                });
              }
            }
            if (legacyVerifyOptions) {
              const createIdx = loadedTemplate.findIndex((t) => t.id === "create_user");
              const migratedList = String(legacyVerifyOptions.authorized_requesters || "").trim();
              if (createIdx >= 0 && migratedList) {
                const createOpts = { ...loadedTemplate[createIdx].options } as Record<string, string>;
                if (!String(createOpts.authorized_requesters || "").trim()) {
                  createOpts.authorized_requesters = migratedList;
                  createOpts.verify_authorized_requester = "true";
                }
                loadedTemplate[createIdx].options = createOpts;
              }
            }
            setTemplateSteps(loadedTemplate);
            setCustomSteps(loadedCustom);
          } catch {
            // keep defaults
          }
        }
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: isClone ? "Failed to load the workflow to copy" : "Failed to load the workflow",
          severity: "error",
        });
      });
  }, [isEdit, id, cloneFromId, isClone, preselectedCustomer]);

  /** Return to list with same filters when we have list `search` from navigation state. */
  const navigateToWorkflowsList = (opts: { afterSave?: boolean } = {}) => {
    const qs = customWorkflowsListSearch;
    if (qs && qs.length > 0) {
      navigate({
        pathname: "/custom_workflows",
        search: qs.startsWith("?") ? qs : `?${qs}`,
      });
      return;
    }
    if (opts.afterSave) {
      navigate("/custom_workflows");
      return;
    }
    navigate(-1);
  };

  const handleBack = () => navigateToWorkflowsList();

  const handleWorkflowTypeChange = (newType: WorkflowType) => {
    if (newType === workflowType) return;
    setWorkflowType(newType);
    setTemplateSteps(
      buildTemplateStepsFromDefinitions(
        getTemplateDefinitions(newType),
        newType === "User Onboarding" ? "onboarding" : "offboarding"
      )
    );
    setCustomSteps([]);
  };

  const setTemplateEnabled = (index: number, enabled: boolean) => {
    setTemplateSteps((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], enabled };
      return next;
    });
  };

  const setTemplateOption = (index: number, key: string, value: string) => {
    setTemplateSteps((prev) => {
      const next = [...prev];
      const opts = { ...(next[index].options || {}), [key]: value };
      next[index] = { ...next[index], options: opts };
      return next;
    });
  };

  const addCustomStep = () => {
    setCustomSteps((prev) => [...prev, { id: `step_${Date.now()}`, action: "", isCustom: true }]);
  };

  const addTechnicianChecklistStep = () => {
    setCustomSteps((previous) => {
      const next = [
        ...previous,
        {
          id: `technician_checklist_${Date.now()}`,
          action: "Technician checklist",
          isCustom: true as const,
          options: { [TECHNICIAN_CHECKLIST_OPTIONS_KEY]: "[]" },
        },
      ];
      setTechnicianChecklistStepIndex(next.length - 1);
      return next;
    });
  };

  const isTechnicianChecklistStep = (step: CustomStepState | undefined) =>
    String(step?.action || "").trim().toLowerCase().replace(/\s+/g, "_") ===
    TECHNICIAN_CHECKLIST_ACTION;

  const technicianChecklistItems = (step: CustomStepState | undefined): TechnicianChecklistItem[] => {
    try {
      const parsed = JSON.parse(String(step?.options?.[TECHNICIAN_CHECKLIST_OPTIONS_KEY] || "[]"));
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((item: any) => ({
          id: String(item?.id || ""),
          title: String(item?.title || "").trim(),
          instructions: String(item?.instructions || "").trim(),
          required: item?.required !== false,
        }))
        .filter((item: TechnicianChecklistItem) => item.id && item.title);
    } catch {
      return [];
    }
  };

  const removeCustomStep = (index: number) => {
    setCustomSteps((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCustomStepAction = (index: number, action: string) => {
    setCustomSteps((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], action };
      return next;
    });
  };

  const handleFlowStepClick = (flowIndex: number, step: any) => {
    // Flow diagram lists all template steps in order, then custom steps (indices align with templateSteps.length).
    if (flowIndex < templateSteps.length) {
      const templateIndex = flowIndex;
      const t = templateSteps[templateIndex];
      if (!t.enabled) {
        setTemplateEnabled(templateIndex, true);
      }
      if (t.id === "assign_groups") {
        setAddToGroupsModalOpen(true);
      } else if (t.id === "assign_sharepoint_site") {
        setAddToSharePointModalOpen(true);
      } else {
        setStepConfigModal({ type: "template", index: templateIndex });
      }
      return;
    }
    const customIndex = flowIndex - templateSteps.length;
    if (isTechnicianChecklistStep(customSteps[customIndex])) {
      setTechnicianChecklistStepIndex(customIndex);
    } else {
      setStepConfigModal({ type: "custom", index: customIndex });
    }
  };

  const handleFlowDeleteStep = (flowIndex: number, step: any) => {
    if (flowIndex < templateSteps.length) {
      setTemplateEnabled(flowIndex, false);
      return;
    }
    const customIndex = flowIndex - templateSteps.length;
    removeCustomStep(customIndex);
  };

  const scheduleDelayConfigFromOptions = (opts: Record<string, string> | undefined) => {
    const enabled = opts?.schedule_delay_enabled === "true";
    return {
      schedule_delay_enabled: enabled ? "true" : "false",
      ...(enabled
        ? {
            schedule_delay_amount: opts?.schedule_delay_amount || "1",
            schedule_delay_unit: opts?.schedule_delay_unit || "hours",
            schedule_delay_reason: opts?.schedule_delay_reason || "",
          }
        : {}),
    };
  };

  const copyScheduleDelayToOptions = (
    opts: Record<string, string>,
    config: {
      schedule_delay_enabled?: string;
      schedule_delay_amount?: string;
      schedule_delay_unit?: string;
      schedule_delay_reason?: string;
    }
  ) => {
    delete opts.schedule_delay_enabled;
    delete opts.schedule_delay_amount;
    delete opts.schedule_delay_unit;
    delete opts.schedule_delay_reason;
    if (config.schedule_delay_enabled !== "true") return;

    opts.schedule_delay_enabled = "true";
    opts.schedule_delay_amount = String(config.schedule_delay_amount || "1");
    opts.schedule_delay_unit = String(config.schedule_delay_unit || "hours");
    const reason = String(config.schedule_delay_reason || "").trim();
    if (reason) {
      opts.schedule_delay_reason = reason;
    }
  };

  const configWithoutScheduleDelay = <T extends Record<string, any>>(config: T) => {
    const {
      schedule_delay_enabled,
      schedule_delay_amount,
      schedule_delay_unit,
      schedule_delay_reason,
      ...rest
    } = config;
    return rest;
  };

  const parseAddToGroupsConfig = (opts: Record<string, string> | undefined): AddToGroupsConfig | null => {
    const scheduleDelay = scheduleDelayConfigFromOptions(opts);
    if (opts?.groups_config) {
      try {
        const parsed = JSON.parse(opts.groups_config) as any;
        const defaultGroups: Array<{ name: string; checked: boolean }> = Array.isArray(parsed?.defaultGroups)
          ? parsed.defaultGroups.map((g: any) => ({
              name: String(g?.name ?? ""),
              checked: Boolean(g?.checked),
            }))
          : [];
        const technician_instruction = String(
          parsed?.technician_instruction ?? parsed?.technicianInstruction ?? ""
        ).trim();
        return { defaultGroups, technician_instruction, ...scheduleDelay };
      } catch {
        /* fall through */
      }
    }
    if (opts?.default_group_names) {
      const names = opts.default_group_names.split(",").map((s) => s.trim()).filter(Boolean);
      return {
        defaultGroups: names.map((name) => ({ name, checked: true })),
        technician_instruction: String(opts?.technician_instruction ?? "").trim(),
        ...scheduleDelay,
      };
    }
    if (scheduleDelay.schedule_delay_enabled === "true") {
      return {
        defaultGroups: [],
        technician_instruction: String(opts?.technician_instruction ?? "").trim(),
        ...scheduleDelay,
      };
    }
    return null;
  };

  const getAssignGroupsStepIndex = () => {
    return templateSteps.findIndex((s) => s.id === "assign_groups");
  };

  const parseAddToSharePointConfig = (
    opts: Record<string, string> | undefined
  ): AddToSharePointSitesConfig | null => {
    const scheduleDelay = scheduleDelayConfigFromOptions(opts);
    if (opts?.sharepoint_config) {
      try {
        const parsed = JSON.parse(opts.sharepoint_config) as any;
        const defaultSites: Array<{ name: string; role: string; checked: boolean }> = Array.isArray(parsed?.defaultSites)
          ? parsed.defaultSites.map((s: any) => ({
              name: String(s?.name ?? ""),
              role: String(s?.role ?? "read"),
              checked: Boolean(s?.checked),
            }))
          : [];
        const technician_instruction = String(
          parsed?.technician_instruction ?? parsed?.technicianInstruction ?? ""
        ).trim();
        return { defaultSites, technician_instruction, ...scheduleDelay };
      } catch {
        /* fall through */
      }
    }
    if (opts?.default_sites) {
      const names = opts.default_sites.split(",").map((s) => s.trim()).filter(Boolean);
      const role = opts.default_role || "read";
      return {
        defaultSites: names.map((name) => ({ name, role, checked: true })),
        technician_instruction: String(opts?.technician_instruction ?? "").trim(),
        ...scheduleDelay,
      };
    }
    if (scheduleDelay.schedule_delay_enabled === "true") {
      return {
        defaultSites: [],
        technician_instruction: String(opts?.technician_instruction ?? "").trim(),
        ...scheduleDelay,
      };
    }
    return null;
  };

  const getAssignSharePointStepIndex = () => {
    return templateSteps.findIndex((s) => s.id === "assign_sharepoint_site");
  };

  const buildStepsForSave = () => {
    const steps: Array<{ id: string; action: string; required: boolean; input_mapping: Record<string, unknown>; options?: Record<string, string> }> = [];
    const normalizeStepOptionsForSave = (rawOptions?: Record<string, string>) => {
      if (!rawOptions || Object.keys(rawOptions).length === 0) return undefined;
      const opts: Record<string, string> = {};
      const scheduleDelayEnabled = rawOptions.schedule_delay_enabled === "true";
      const useCustomPassword = rawOptions.use_custom_password === "true";
      for (const [k, v] of Object.entries(rawOptions)) {
        if (k === "password") continue;
        if (k === "custom_password" && !useCustomPassword) continue;
        if (k.startsWith("schedule_delay_") && k !== "schedule_delay_enabled" && !scheduleDelayEnabled) continue;
        const val = String(v ?? "").trim();
        const includeKey =
          [
            "use_custom_password",
            "force_change_next_sign_in",
            "generate_onetime_link",
            "check_recent_duplicates",
            "verify_authorized_requester",
            "auto_add_to_new",
            "auto_add_to_reply",
            "auto_add_to_forward",
            "auto_add_on_mobile",
          ].includes(k) ||
          (k === "schedule_delay_enabled" && scheduleDelayEnabled) ||
          (k === "custom_password" && useCustomPassword) ||
          val;
        if (includeKey) opts[k] = val;
      }
      return Object.keys(opts).length > 0 ? opts : undefined;
    };
    // Always serialize enabled template steps — not only for "User Onboarding".
    // If we gated on workflow type, saving as "User Offboarding" would omit template
    // steps and wipe sharepoint_config / groups_config / password options in workflow_step_definitions.
    for (const t of templateSteps) {
      if (!t.enabled) continue;
      const step: any = {
        id: t.id,
        action: t.action,
        required: true,
        input_mapping: {},
      };
      const opts = normalizeStepOptionsForSave(t.options);
      if (opts) step.options = opts;
      steps.push(step);
    }
    for (const c of customSteps) {
      const act = c.action.trim().toLowerCase().replace(/\s+/g, "_");
      if (!act) continue;
      const step: any = {
        id: c.id,
        action: act,
        required: true,
        input_mapping: {},
      };
      const opts = normalizeStepOptionsForSave(c.options);
      if (opts) step.options = opts;
      steps.push(step);
    }
    return steps;
  };

  const handleSave = async () => {
    if (!workflowName.trim()) {
      setSnackbar({ open: true, message: "Workflow name is required", severity: "warning" });
      return;
    }
    if (!customerId) {
      setSnackbar({ open: true, message: "Please select a customer", severity: "warning" });
      return;
    }
    if (
      customSteps.some(
        (step) => isTechnicianChecklistStep(step) && technicianChecklistItems(step).length === 0
      )
    ) {
      setSnackbar({
        open: true,
        message: "Every Technician checklist step must contain at least one task",
        severity: "warning",
      });
      return;
    }
    const steps = buildStepsForSave();
    if ((workflowType === "User Onboarding" || workflowType === "User Offboarding") && steps.length === 0) {
      setSnackbar({ open: true, message: "At least one step must be enabled", severity: "warning" });
      return;
    }
    setSaving(true);
    try {
      const userEmail = (user as any)?.email || (user as any)?.name || "Unknown";
      const payload = {
        customer_id: Number(customerId),
        workflow_name: workflowName.trim(),
        workflow_type: workflowType,
        steps_json: { steps, auto_continue_hitl: autoContinueHitl },
        last_updated_by: userEmail,
        created_by: userEmail,
      };
      if (isEdit && id) {
        await customWorkflowApi.updateWorkflow({ ...payload, id: Number(id) });
        setSnackbar({ open: true, message: "Workflow updated successfully", severity: "success" });
      } else {
        await customWorkflowApi.createWorkflow(payload);
        setSnackbar({ open: true, message: "Workflow created successfully", severity: "success" });
      }
      setTimeout(() => navigateToWorkflowsList({ afterSave: true }), 800);
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || "Failed to save workflow";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#705D4A" } },
  };

  const selectedCustomerName =
    filteredCustomers.find((customer) => customer.id?.toString() === customerId)?.name || "";
  const supportSnapshot = useMemo(
    () => ({
      surface: "custom_workflow" as const,
      title: workflowName.trim() ? `Custom workflow: ${workflowName.trim()}` : "New custom workflow",
      configuration: {
        workflow_id: id || null,
        editing: isEdit,
        cloning: isClone,
        name: workflowName,
        workflow_type: workflowType,
        auto_continue_hitl: autoContinueHitl,
        customer_id: customerId,
        customer_name: selectedCustomerName,
        scheduler_enabled: schedulerEnabled,
        saving,
        notice: snackbar.open ? snackbar.message : "",
        enabled_template_steps: templateSteps
          .filter((step) => step.enabled)
          .map((step) => ({
            id: step.id,
            action: step.action,
            label: step.label,
            options: step.options || {},
          })),
        disabled_template_steps: templateSteps.filter((step) => !step.enabled).map((step) => step.label),
        custom_steps: customSteps.map((step) => ({
          id: step.id,
          action: step.action,
          options: step.options || {},
        })),
      },
    }),
    [
      id,
      isEdit,
      isClone,
      workflowName,
      workflowType,
      autoContinueHitl,
      customerId,
      selectedCustomerName,
      schedulerEnabled,
      saving,
      snackbar,
      templateSteps,
      customSteps,
    ]
  );
  usePublishConfigurationSupport("custom-workflow-form", supportSnapshot, 30);

  return (
    <Box sx={{ px: 1, pt: 2, maxWidth: 960, width: "100%", fontFamily: "var(--theo-font-sans)", overflow: "visible" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ fontFamily: "var(--theo-font-sans)", color: "#705D4A", textTransform: "none" }}
        >
          Back
        </Button>
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 600, color: "#333", mb: 2, fontFamily: "var(--theo-font-sans)" }}>
        {isEdit ? `Edit ${workflowType} Workflow` : isClone ? "Copy Custom Workflow" : "Create Custom Workflow"}
      </Typography>

      {isClone && (
        <Alert severity="info" sx={{ mb: 2, fontFamily: "var(--theo-font-sans)" }}>
          Review customer-specific settings such as domains, groups, SharePoint sites, managers, and email templates before creating this copy.
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, overflow: "visible" }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start", width: "100%" }}>
          <TextField
            label="Workflow Name"
            value={workflowName}
            onChange={(e) => setWorkflowName(e.target.value)}
            placeholder="e.g. User Onboarding"
            required
            sx={{
              flex: "2 1 360px",
              minWidth: 280,
              maxWidth: "100%",
              ...textFieldSx,
            }}
            inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
            InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
          />
          <LayeredAutocompleteSelect
            value={customerId}
            label="Customer"
            onChange={value => {
                const v = value as string;
                setCustomerId(v);
              }}
            sx={{ ...({ flex: "1 1 280px", minWidth: 240, maxWidth: "100%", overflow: "visible" }), ...({ "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#705D4A" } }) }}
            disableClearable
            options={filteredCustomers.map(customer => ({ value: customer.id != null ? String(customer.id) : "", label: customer.name?.toLowerCase() === "root" ? "All Customer" : customer.name }))}
          />
          <FormControl sx={{ flex: "1 1 220px", minWidth: 200, maxWidth: "100%" }}>
            <InputLabel id="custom-workflow-type-label">Workflow Type</InputLabel>
            <Select
              labelId="custom-workflow-type-label"
              value={workflowType}
              label="Workflow Type"
              onChange={(e) => handleWorkflowTypeChange(e.target.value as WorkflowType)}
              MenuProps={{ disableScrollLock: true, PaperProps: { sx: { zIndex: 9999 } } }}
              sx={{ "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#705D4A" } }}
            >
              {WORKFLOW_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 1, px: 2, py: 1.25, bgcolor: "#FAFAFA" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={autoContinueHitl}
                onChange={(event) => setAutoContinueHitl(event.target.checked)}
              />
            }
            label="Auto-continue HITL approvals"
          />
          <Typography variant="body2" sx={{ color: "#666", fontFamily: "var(--theo-font-sans)", ml: 4 }}>
            Automatically executes approval-gated workflow actions without waiting for a technician to click Continue.
            Pre-verification failures and information requests still stop the workflow.
          </Typography>
        </Box>

        {(workflowType === "User Onboarding" || workflowType === "User Offboarding") && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: "var(--theo-font-sans)" }}>
                Steps
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", fontFamily: "var(--theo-font-sans)" }}>
                {workflowType === "User Onboarding"
                  ? viewMode === "flow"
                    ? "Click a step to configure it."
                    : "Standard onboarding template. Enable or disable steps and customize options."
                  : viewMode === "flow"
                    ? "Click a step to configure it."
                    : "Standard M365 offboarding. Steps 1–2 (block sign-in, then revoke sessions) must stay first when enabled. Some runbook items are manual — see the note below."}
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
              sx={{ "& .Mui-selected": { color: "#705D4A", bgcolor: "#FFFBF8" } }}
            >
              <ToggleButton value="flow">
                <AccountTreeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                Flow
              </ToggleButton>
              <ToggleButton value="list">
                <ViewListIcon sx={{ mr: 0.5, fontSize: 18 }} />
                List
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {viewMode === "flow" && (
            <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 1, p: 2, bgcolor: "#FAFAFA", mb: 2 }}>
              <CustomWorkflowFlowDiagram
                templateSteps={templateSteps}
                customSteps={customSteps}
                onStepClick={handleFlowStepClick}
                onAddStep={addCustomStep}
                onAddTechnicianChecklist={addTechnicianChecklistStep}
                onDeleteStep={handleFlowDeleteStep}
                schedulerEnabled={schedulerEnabled}
              />
            </Box>
          )}

          {viewMode === "list" && (
            <>
          {templateSteps.map((step, index) => (
            <Box
              key={step.id}
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: 1,
                p: 1.5,
                mb: 1,
                backgroundColor: step.enabled ? "#FAFAFA" : "#F5F5F5",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={step.enabled}
                    onChange={(e) => setTemplateEnabled(index, e.target.checked)}
                    sx={{ "&.Mui-checked": { color: "#705D4A" } }}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 500 }}>
                      {index + 1}. {stripLeadingWorkflowStepNumber(step.label)}
                    </Typography>
                    {step.fields && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "#888",
                          fontFamily: "var(--theo-font-sans)",
                          fontSize: "0.75rem",
                          mt: 0.25,
                        }}
                      >
                        {step.fields}
                      </Typography>
                    )}
                  </Box>
                }
              />

              {step.enabled &&
                (step.id === "assign_groups" ||
                  step.id === "assign_sharepoint_site" ||
                  step.id === "create_user" ||
                  step.id === "set_password" ||
                  step.id === "offboard_reset_password" ||
                  step.id === "send_welcome_email" ||
                  step.id === "itglue_add_password" ||
                  step.id === "set_manager" ||
                  step.id === "add_to_team") && (
                <Box sx={{ ml: 4, mt: 1 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      if (step.id === "assign_groups") setAddToGroupsModalOpen(true);
                      else if (step.id === "assign_sharepoint_site") setAddToSharePointModalOpen(true);
                      else setStepConfigModal({ type: "template", index });
                    }}
                    sx={{ fontFamily: "var(--theo-font-sans)", color: "#705D4A", textTransform: "none" }}
                  >
                    Configure
                  </Button>
                  {step.id === "assign_sharepoint_site" && (
                    <>
                      {(() => {
                        const defaultSitesRaw = step.options?.default_sites || "";
                        const defaultRole = (step.options?.default_role || "read").trim();
                        const sites = defaultSitesRaw
                          .split(",")
                          .map((x) => x.trim())
                          .filter(Boolean);
                        if (sites.length === 0) return null;

                        const displayCount = 2;
                        const displaySites = sites.slice(0, displayCount).join(", ");
                        const moreCount = sites.length - displayCount;

                        return (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", color: "#888", fontFamily: "var(--theo-font-sans)", mt: 0.5 }}
                          >
                            Default sites: {displaySites}
                            {moreCount > 0 ? ` (+${moreCount} more)` : ""} (role: {defaultRole})
                          </Typography>
                        );
                      })()}
                    </>
                  )}
                </Box>
              )}

              {step.enabled &&
                getStepDefaultFieldSpecs(step.id).length > 0 &&
                step.id !== "assign_groups" &&
                step.id !== "assign_sharepoint_site" &&
                step.id !== "send_welcome_email" &&
                step.id !== "itglue_add_password" &&
                step.id !== "set_manager" &&
                step.id !== "add_to_team" && (
                <Box sx={{ ml: 4, mt: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {getStepDefaultFieldSpecs(step.id).map((spec) => (
                    <TextField
                      key={spec.key}
                      size="small"
                      label={spec.label}
                      placeholder={spec.placeholder}
                      multiline={spec.multiline}
                      minRows={spec.multiline ? spec.minRows ?? 4 : undefined}
                      maxRows={spec.multiline ? 16 : undefined}
                      value={step.options?.[spec.key] ?? ""}
                      onChange={(e) => setTemplateOption(index, spec.key, e.target.value)}
                      type={spec.multiline ? undefined : spec.key === "password" ? "password" : "text"}
                      sx={{ ...textFieldSx, maxWidth: 400 }}
                      inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                      InputLabelProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          ))}

          {customSteps.map((step, index) => {
            const stepNumber = templateSteps.length + index + 1;
            return (
              <Box
                key={step.id}
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: 1,
                  p: 1.5,
                  mb: 1,
                  backgroundColor: "#FAFAFA",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 500, pt: 0.75, minWidth: 24 }}>
                  {stepNumber}.
                </Typography>
                {isTechnicianChecklistStep(step) ? (
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600 }}>
                      Technician checklist
                    </Typography>
                    <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#666", fontSize: 13 }}>
                      {technicianChecklistItems(step).length} technician task(s)
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setTechnicianChecklistStepIndex(index)}
                      sx={{ textTransform: "none", mt: 0.5 }}
                    >
                      Configure checklist
                    </Button>
                  </Box>
                ) : (
                  <TextField
                    size="small"
                    placeholder="e.g. add to team, set manager"
                    value={step.action}
                    onChange={(e) => updateCustomStepAction(index, e.target.value)}
                    fullWidth
                    sx={textFieldSx}
                    inputProps={{ style: { fontFamily: "var(--theo-font-sans)" } }}
                  />
                )}
                <IconButton size="small" onClick={() => removeCustomStep(index)} sx={{ color: "#666", mt: -0.5 }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}

          <Button
            startIcon={<AddIcon />}
            onClick={addCustomStep}
            sx={{ fontFamily: "var(--theo-font-sans)", color: "#705D4A", textTransform: "none", mt: 1 }}
          >
            Add Theo Step
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={addTechnicianChecklistStep}
            sx={{ fontFamily: "var(--theo-font-sans)", color: "#705D4A", textTransform: "none", mt: 1, ml: 1 }}
          >
            Add Technician Checklist
          </Button>
            </>
          )}
        </Box>
        )}

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <NeoButton variant="neo" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <CircularProgress size={18} sx={{ color: "white", mr: 1 }} />
                {isEdit ? "Updating..." : "Creating..."}
              </>
            ) : (
              isEdit ? "Update" : "Create"
            )}
          </NeoButton>
          <Button onClick={handleBack} sx={{ fontFamily: "var(--theo-font-sans)", color: "#666" }}>
            Cancel
          </Button>
        </Box>
      </Box>

      <AddToGroupsModal
        open={addToGroupsModalOpen}
        onClose={() => setAddToGroupsModalOpen(false)}
        onSave={(config) => {
          const idx = getAssignGroupsStepIndex();
          if (idx >= 0) {
            setTemplateSteps((prev) => {
              const next = [...prev];
              const opts = { ...next[idx].options };
              copyScheduleDelayToOptions(opts, config);
              opts.groups_config = JSON.stringify(configWithoutScheduleDelay(config));
              opts.default_group_names = config.defaultGroups
                .filter((g) => g.checked)
                .map((g) => g.name)
                .join(", ");
              opts.technician_instruction = String(config.technician_instruction ?? "").trim();
              next[idx] = { ...next[idx], options: opts };
              return next;
            });
          }
        }}
        initialConfig={parseAddToGroupsConfig(templateSteps[getAssignGroupsStepIndex()]?.options)}
        schedulerEnabled={schedulerEnabled}
      />

      <AddToSharePointSitesModal
        open={addToSharePointModalOpen}
        onClose={() => setAddToSharePointModalOpen(false)}
        onSave={(config) => {
          const idx = getAssignSharePointStepIndex();
          if (idx >= 0) {
            setTemplateSteps((prev) => {
              const next = [...prev];
              const opts = { ...next[idx].options };
              copyScheduleDelayToOptions(opts, config);
              opts.sharepoint_config = JSON.stringify(configWithoutScheduleDelay(config));
              opts.default_sites = config.defaultSites
                .filter((s) => s.checked && s.name.trim())
                .map((s) => s.name.trim())
                .join(", ");
              const firstRole = config.defaultSites.find((s) => s.checked)?.role ?? "read";
              opts.default_role = firstRole;
              opts.technician_instruction = String(config.technician_instruction ?? "").trim();
              next[idx] = { ...next[idx], options: opts };
              return next;
            });
          }
        }}
        initialConfig={parseAddToSharePointConfig(
          templateSteps[getAssignSharePointStepIndex()]?.options
        )}
        schedulerEnabled={schedulerEnabled}
      />

      {technicianChecklistStepIndex !== null && (
        <TechnicianChecklistModal
          open
          initialItems={technicianChecklistItems(customSteps[technicianChecklistStepIndex])}
          onClose={() => setTechnicianChecklistStepIndex(null)}
          onSave={(items) => {
            setCustomSteps((previous) =>
              previous.map((step, index) =>
                index === technicianChecklistStepIndex
                  ? {
                      ...step,
                      action: "Technician checklist",
                      options: {
                        ...(step.options || {}),
                        [TECHNICIAN_CHECKLIST_OPTIONS_KEY]: JSON.stringify(items),
                      },
                    }
                  : step
              )
            );
            setTechnicianChecklistStepIndex(null);
          }}
        />
      )}

      {stepConfigModal && (
        <StepConfigModal
          open={true}
          onClose={() => setStepConfigModal(null)}
          stepLabel={
            stepConfigModal.type === "template"
              ? stripLeadingWorkflowStepNumber(templateSteps[stepConfigModal.index]?.label ?? "")
              : customSteps[stepConfigModal.index]?.action ?? ""
          }
          stepId={
            stepConfigModal.type === "template"
              ? templateSteps[stepConfigModal.index]?.id ?? ""
              : "custom"
          }
          options={
            stepConfigModal.type === "template"
              ? templateSteps[stepConfigModal.index]?.options ?? {}
              : { ...(customSteps[stepConfigModal.index]?.options ?? {}), action: customSteps[stepConfigModal.index]?.action ?? "" }
          }
          defaultFields={
            stepConfigModal.type === "template"
              ? getStepDefaultFieldSpecs(templateSteps[stepConfigModal.index]?.id ?? "")
              : [{ key: "action", label: "Action name", placeholder: "e.g. add to team, set manager" }]
          }
          isSetPassword={
            stepConfigModal.type === "template" &&
            (templateSteps[stepConfigModal.index]?.id === "set_password" ||
              templateSteps[stepConfigModal.index]?.id === "offboard_reset_password")
          }
          isCreateUser={
            stepConfigModal.type === "template" &&
            templateSteps[stepConfigModal.index]?.id === "create_user"
          }
          isWelcomeEmail={
            stepConfigModal.type === "template" &&
            templateSteps[stepConfigModal.index]?.id === "send_welcome_email"
          }
          isItGluePassword={
            stepConfigModal.type === "template" &&
            templateSteps[stepConfigModal.index]?.id === "itglue_add_password"
          }
          isSetManager={
            stepConfigModal.type === "template" &&
            templateSteps[stepConfigModal.index]?.id === "set_manager"
          }
          isAddToTeam={
            stepConfigModal.type === "template" &&
            templateSteps[stepConfigModal.index]?.id === "add_to_team"
          }
          isEmailSignature={
            stepConfigModal.type === "template" &&
            templateSteps[stepConfigModal.index]?.id === "set_email_signature"
          }
          defaultDomainForCustomer={
            stepConfigModal.type === "template" &&
            templateSteps[stepConfigModal.index]?.id === "create_user"
              ? customerDomain
              : undefined
          }
          schedulerEnabled={schedulerEnabled}
          onSave={(options) => {
            if (stepConfigModal.type === "template") {
              const idx = stepConfigModal.index;
              setTemplateSteps((prev) => {
                const next = [...prev];
                next[idx] = { ...next[idx], options: { ...next[idx].options, ...options } };
                return next;
              });
            } else {
              const idx = stepConfigModal.index;
              const action = options.action ?? customSteps[idx]?.action ?? "";
              const savedOptions = { ...options };
              delete savedOptions.action;
              updateCustomStepAction(idx, action);
              setCustomSteps((prev) => {
                const next = [...prev];
                next[idx] = {
                  ...next[idx],
                  options: Object.keys(savedOptions).length > 0 ? savedOptions : undefined,
                };
                return next;
              });
            }
            setStepConfigModal(null);
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ fontFamily: "var(--theo-font-sans)" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomWorkflowForm;
